import { useQuery } from "@apollo/client";
import { useEffect } from "react";
import { GOOGLE_CONNECTION_HEALTH_QUERY } from "@/graphql/google-business";
import { useSalonStore } from "@/lib/store/salon";

/**
 * Cadence for the health query. We poll fast while the initial-sync job is
 * still running (the user just clicked Link and is staring at an empty list)
 * so the dashboard learns about completion within seconds rather than within
 * a minute. As soon as the connection reports any `lastSyncAt`, we drop back
 * to the slow cadence — there's no UX reason to keep hammering at 5s once
 * the first batch of reviews is on screen.
 */
const FAST_POLL_MS = 5_000;
const SLOW_POLL_MS = 60_000;

export function useGoogleConnectionHealth() {
  const { currentSalon } = useSalonStore();
  const salonId = currentSalon?.id;

  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery(
    GOOGLE_CONNECTION_HEALTH_QUERY,
    {
      variables: { salonId: salonId ?? "" },
      skip: !salonId,
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "cache-and-network",
    }
  );

  const health = data?.googleConnectionHealth ?? null;
  // "Initial sync pending" = connected, but the worker hasn't written a
  // `lastSyncAt` yet. That's the only window worth fast-polling.
  const pendingInitialSync = Boolean(health?.hasConnection && !health.lastSyncAt);

  useEffect(() => {
    if (!salonId) return;
    startPolling(pendingInitialSync ? FAST_POLL_MS : SLOW_POLL_MS);
    return () => stopPolling();
  }, [salonId, pendingInitialSync, startPolling, stopPolling]);

  return {
    health,
    loading,
    error,
    refetch,
  };
}
